"use client";

import React from "react";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { ShoppingCart, QrCode, Wifi } from "lucide-react";

/**
 * WanderLite "How it works" — three friendly numbered cards with blue numbered
 * circles. Soft white cards, pastel sky section, plain language. Presentational.
 */
export default function HowItWorksSteps() {
  const { t } = useTranslations();

  const steps = [
    {
      icon: ShoppingCart,
      title: t("wander.how.s1Title", "Choose a Plan"),
      body: t("wander.how.s1Body", "Select your destination and pick the data plan that fits your trip."),
    },
    {
      icon: QrCode,
      title: t("wander.how.s2Title", "Scan QR Code"),
      body: t("wander.how.s2Body", "Receive your eSIM QR code instantly by email and scan it to install."),
    },
    {
      icon: Wifi,
      title: t("wander.how.s3Title", "Connect & Enjoy"),
      body: t("wander.how.s3Body", "Turn it on when you arrive and enjoy data wherever you are."),
    },
  ];

  return (
    <section className="wander-sky py-14">
      <div className="container mx-auto px-4">
        <Reveal as="div" className="mb-10 max-w-xl" y={16}>
          <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
            {t("wander.how.title", "Get connected in 3 simple steps")}
          </h2>
          <p className="mt-3 text-muted-foreground">
            {t("wander.how.subtitle", "From checkout to connected in minutes — no SIM swap required.")}
          </p>
        </Reveal>

        <Stagger as="div" className="grid gap-5 md:grid-cols-3" staggerChildren={0.1}>
          {steps.map(({ icon: Icon, title, body }, i) => (
            <StaggerItem as="div" key={title} y={16} className="wander-card relative flex flex-col gap-3 p-7 transition-all hover:-translate-y-1">
              <span className="absolute right-6 top-6 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                {i + 1}
              </span>
              <span className="wander-chip inline-flex h-12 w-12 items-center justify-center">
                <Icon className="h-5 w-5" />
              </span>
              <div className="text-lg font-semibold text-foreground">{title}</div>
              <p className="text-sm text-muted-foreground">{body}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
